import { useEffect, useRef } from "react";
import { Check, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { btnGhost, btnPrimary, Panel } from "../ui";
import { JobProgress, useJob } from "../useJob";

const secs = (a, b) => (a && b ? `${Math.max(1, Math.round((new Date(b) - new Date(a)) / 1000))} s` : "");

const Step = ({ n, label, model, state, time, testId }) => (
  <li data-testid={testId} data-state={state} className="flex items-center gap-3 text-sm">
    <span className={`w-6 h-6 shrink-0 flex items-center justify-center text-[11px] ${state === "done" ? "bg-[#3d5a3a] text-white" : state === "run" ? "bg-[#956842] text-white" : state === "fail" ? "bg-[#8a2f22] text-white" : "border border-[#D5B98C] text-[#956842]"}`}>
      {state === "done" ? <Check className="w-3.5 h-3.5" /> : state === "run" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : n}
    </span>
    <span className="flex-1"><b>{label}</b> <span className="text-xs text-[#242321]/55">· {model}</span></span>
    <span className="text-xs text-[#242321]/60">{time}</span>
  </li>
);

const AiSteps = ({ job, draft }) => {
  if (!job && !draft) return null;
  const stage = job?.stage || draft?.stage;
  const failed = job?.status === "errore";
  const draftDone = ["rifinitura", "rifinito"].includes(stage) || job?.status === "completato" || (!job && !!draft);
  return (
    <ol className="space-y-2 mb-4 p-3 bg-[#F5F0E8]/70 border border-[#D5B98C]/50" data-testid="ai-steps">
      <Step n="1" label="Bozza pronta" model="Gemini 3 Flash" testId="ai-step-draft" time={secs(job?.started_draft_at, job?.draft_at)}
        state={draftDone ? "done" : failed ? "fail" : job ? "run" : "todo"} />
      <Step n="2" label="Testi rifiniti" model="Claude Opus 5.5" testId="ai-step-refine" time={secs(job?.draft_at, job?.refined_at)}
        state={stage === "rifinito" ? "done" : job?.refine_error || failed ? "fail" : stage === "rifinitura" ? "run" : "todo"} />
      {job?.refine_error && <li className="text-xs text-[#8a2f22]" data-testid="ai-refine-error">{job.refine_error}</li>}
    </ol>
  );
};

export const AiDraftPanel = ({ product, autoStart, onAiUpdate }) => {
  const draft = product.ai_draft;
  const started = useRef(false);
  const pull = (msg) => api.get(`/admin/products/${product.id}`).then((r) => { onAiUpdate(r.data); if (msg) toast.success(msg); });
  const onTick = (next, prev) => { if (next.stage !== prev.stage && next.stage === "rifinitura") pull("Bozza pronta: campi compilati. Rifinitura dei testi in corso…"); };
  const { job, setJob, retry, running } = useJob(() => pull("Testi rifiniti: controlla e approva"), onTick);

  const start = async (fields) => {
    try { setJob((await api.post(`/admin/products/${product.id}/analyze`, fields ? { fields } : {})).data); } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (product.ai_job_id) api.get(`/admin/jobs/${product.ai_job_id}`).then((r) => { if (!["completato", "errore"].includes(r.data.status) || !draft) setJob(r.data); }).catch(() => {});
    else if (autoStart && !draft && (product.photos?.length || product.images?.length)) start();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Panel title="Scheda AI" testId="ai-draft-panel" actions={<Sparkles className="w-5 h-5 text-[#956842]" strokeWidth={1.4} />}>
      <div className="flex flex-wrap gap-2 mb-4">
        <button data-testid="ai-analyze-button" disabled={running} onClick={() => start()} className={btnPrimary}><Sparkles className="w-4 h-4" /> {draft ? "Riscrivi tutto con AI" : "Crea scheda con AI"}</button>
        {draft && <button data-testid="ai-regen-title" disabled={running} onClick={() => start(["name", "seo_title"])} className={btnGhost}><RefreshCw className="w-3.5 h-3.5" /> Nuovo titolo</button>}
        {draft && <button data-testid="ai-regen-description" disabled={running} onClick={() => start(["short_description", "description", "meta_description"])} className={btnGhost}><RefreshCw className="w-3.5 h-3.5" /> Nuova descrizione</button>}
      </div>
      <AiSteps job={job} draft={draft} />
      <JobProgress job={job} onRetry={() => retry().catch((e) => toast.error(apiError(e)))} label="Scheda AI" testId="ai-job" />
      <p className="text-sm text-[#242321]/60" data-testid="ai-hint">
        {draft ? "I campi sono già compilati: leggi, modifica se serve e premi \"Approva e pubblica\"." : "L'AI guarda le foto e compila la scheda da sola. Il prezzo lo inserisci tu."}
      </p>
    </Panel>
  );
};
